import React from 'react'
import Layout from '../components/layout/Layout'
import Seo from '../components/Seo'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

const Terms: React.FC = () => {
  const updatedAt = '11 Dec 2025'
  return (
    <Layout>
      <Seo title="Terms of Service — 2homes.app" canonical="https://2homes.app/terms" robots="index,follow" />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-4">
          <Link to="/" className="inline-flex items-center text-slate-700 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to home
          </Link>
        </div>
        <div className="card-glass p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            Terms of Service 2homes.app
          </h1>
          <p className="text-sm text-slate-500 mb-8">Last updated: {updatedAt}</p>

          <div className="prose prose-slate max-w-none">
            <h2><strong>1. General Provisions</strong></h2>
            <p>
              The Administrator of the 2homes.app Service is AITRAIN Marcin Opinc, VAT ID (NIP) 5831541445, (no KRS),
              correspondence address: ul. Kaszubska 6/21, 83-110 Tczew, Poland, e‑mail: contact@2homes.app.
              These Terms define the rules for using the Application and paid and unpaid services.
              Acceptance of the Terms is a condition for using the Service.
            </p>

            <h2><strong>2. Definitions</strong></h2>
            <ul>
              <li>"Application" / "Service" – 2homes.app.</li>
              <li>"User" – a person with an account.</li>
              <li>"Family" – a shared space for Users (parents, viewers).</li>
              <li>"Family Administrator" – User creating the family and managing permissions.</li>
              <li>"Family Data" – calendar entries, children, files, notes, roles, and permissions.</li>
              <li>"Free Plan" / "Paid Plan" – subscription variants of the Application.</li>
              <li>"Lifetime Account" / "Founder Account" – a one-time payment plan that grants access to Pro features for the lifetime of the Service (Product Lifetime), as defined below.</li>
              <li>"Product Lifetime" – the period during which the Service is publicly offered and supported by the Service Provider. It does not refer to the lifetime of the User.</li>
            </ul>

            <h2><strong>3. Account, Age, Responsibility, and Consents</strong></h2>
            <ul>
              <li>Accounts can be created by adults.</li>
              <li>By creating an account, you accept the Terms of Service and confirm that you have read the Privacy Policy. Your acceptance is recorded with a version number and timestamp.</li>
              <li>During registration, you may optionally consent to receive marketing communications (newsletters, special offers). This consent is voluntary and can be withdrawn at any time without affecting your use of the Service.</li>
              <li>The Family Administrator declares that they have the right to process Family Data (including children's data) and grant access to invited persons.</li>
              <li>The User is responsible for the accuracy of data and the security of access to the account/device.</li>
            </ul>

            <h2><strong>4. Scope and Nature of Services</strong></h2>
            <p>The Application provides organizational tools (calendar, sharing, exports) and does not provide legal, mediation, or advisory services. It does not replace settlements/agreements or judgments.</p>

            <h2><strong>5. Plans and Limits</strong></h2>
            <p>
              Detailed specifications of available plans and limits can be found in <strong>Annex A – Plan Specification</strong> below.
              The following provisions are general.
            </p>
            <ul className="list-disc ml-5 space-y-1">
              <li>The Basic (Solo) Plan is available to every registered user (designed for one parent).</li>
              <li>Available features and limits depend on the selected plan.</li>
              <li>Changing the plan may result in enforcing limits (e.g., number of children, history).</li>
              <li>If a Pro plan expires (return to Basic), any existing second parent (Parent B) will be automatically downgraded to Viewer (read-only) status until the subscription is renewed.</li>
              <li>The Service may apply technical limits (rate‑limit, file sizes, anti‑scraping).</li>
              <li>
                <strong>Trial Access:</strong> Newly created accounts may receive temporary access to Pro features for a limited time (e.g., 14 days)
                without requiring a payment method. After this period, if no subscription is purchased, the account will automatically
                revert to the basic plan limits.
              </li>
            </ul>

            <h2><strong>6. Payments and Billing</strong></h2>
            <p>
              Payments are made electronically. The total price including taxes
              and any additional fees is presented before placing the order/starting the subscription
              on the <a href="/pricing">Pricing</a> page and in the payment interface. The subscription renews automatically until canceled by the User.
            </p>
            <p>
              The 2homes.app Service is intended exclusively for consumers (natural persons using the Service for purposes not directly related
              to their trade, business, craft, or profession). We do not offer B2B sales and do not issue VAT invoices to companies or entrepreneurs.
            </p>
            <ul>
              <li>
                Invoice/bill: issued and sent electronically to the account's e‑mail address
                upon your request submitted via e‑mail to contact@2homes.app.
              </li>
              <li>Lack of payment or cancellation results in a return to the Free plan and enforcement of limits – without deleting data.</li>
              <li>Moment of concluding the contract: upon confirmation of payment by the operator, and access to paid plan features is activated immediately after authorization.</li>
            </ul>

            <h2><strong>7. Plan Change and Limit Enforcement</strong></h2>
            <ul>
              <li>Change to Free with exceeded limits: 1 child indicated by the Family Administrator remains active (if not indicated – the first child).</li>
              <li>Viewers are suspended (access paused) until the Paid plan is reactivated.</li>
              <li>History may be hidden in the UI; GDPR export remains available to the Family Administrator.</li>
            </ul>

            <h2><strong>8. User Content and Responsibility</strong></h2>
            <p>The User retains rights to the Content and grants the Service a non-exclusive license to process them solely for the purpose of providing services (hosting, backup, generating exports). It is forbidden to post unlawful content, violating children's rights or sensitive data without a legal basis. It is also forbidden to publish vulgar, offensive, hate speech, discriminatory content, or content violating the personal rights of others (entries, notes, events, etc.).</p>

            <h2><strong>9. Privacy and GDPR Roles</strong></h2>
            <p>The Privacy Policy is an integral part of the Terms.</p>
            <ul>
              <li>In terms of account data (login, billing), the Service is the controller.</li>
              <li>In terms of Family Data, the Service acts as a processor on behalf of the Family Administrator. On request, we provide a Data Processing Agreement (DPA).</li>
              <li>The Service uses sub-processors – list in Annex B. Production data is stored in the EU (region eu‑central‑2).</li>
            </ul>
            <p>
              Privacy Policy:{' '}
              <a href="/privacy-policy" className="text-blue-600 hover:underline">read on our website</a>
            </p>

            <h2><strong>10. Security, Encryption, and Limitations</strong></h2>
            <ul>
              <li>Data is transmitted using TLS; data at rest is encrypted on the infrastructure provider's side.</li>
              <li>The Application may offer E2EE functions for selected data; key management is on the User's side. Loss of password/key may make it impossible to recover part of the data.</li>
              <li>PWA/Service Worker may cache files on the User's device; we recommend logging out on shared devices.</li>
            </ul>

            <h2><strong>11. Availability, Maintenance, and Changes</strong></h2>
            <p>The Service is provided on an "as is/as available" basis without a guarantee of uninterrupted operation. Possible technical breaks, updates, and functional changes (including limits) – with information provided as far as possible.</p>
            <p>
              <strong>Termination of Service:</strong> The Service Provider reserves the right to discontinue the Service at any time. In the event of a decision to end the Service (End of Life), Users with Lifetime Accounts will be notified at least 6 months in advance.
            </p>
            <p>
              <strong>Minimum Guarantee:</strong> If the Service is discontinued within 3 years of the Lifetime Account purchase date, the User is entitled to a proportional refund.
            </p>
            <p>
              <strong>Future Features:</strong> The Lifetime Account includes functionalities available in the Pro plan at the time of purchase and their future updates. The Service Provider reserves the right that future, new modules or functionalities that are not part of the current offering (particularly services based on external, separately paid APIs) may be offered as separately paid extensions (Add-ons) and do not form part of the purchased package.
            </p>

            <h2><strong>12. Permitted Use</strong></h2>
            <p>Prohibited: reverse engineering, automatic scraping, bypassing limits, sharing access with unauthorized persons, actions violating security.</p>

            <h2><strong>13. Intellectual Property</strong></h2>
            <p>UI, code, logos, and Service materials are protected. We grant a non-exclusive license to use the Application in accordance with the Terms.</p>

            <h2><strong>14. Reporting Violations and Abuse</strong></h2>
            <p>Reports: contact@2homes.app. We may temporarily block access to disputed content until clarified.</p>

            <h2><strong>15. Liability and Limitation of Claims</strong></h2>
            <p>To the fullest extent permitted by law, the Service is not liable for the consequences of using the Application in family/legal disputes, Users' decisions, and indirect damages/lost profits. The total liability of the Service is limited to the equivalent of the User's last 3 monthly fees (and in the Free plan – up to 100 PLN), except for liability that cannot be excluded.</p>

            <h2><strong>16. Resignation, Withdrawal, Complaints, and Data Deletion</strong></h2>
            <p>
              The User may stop using the service at any time and request data deletion in accordance with the Privacy Policy.
              In the case of services provided in a subscription model and activated immediately after payment, the User
              agrees to the commencement of the service before the withdrawal period expires and acknowledges
              that after full performance of the service, they lose the right to withdraw from the contract in accordance with Art. 38 of the Consumer Rights Act.
            </p>
            <ul>
              <li>Withdrawal: possible if the service has not fully started and the User has not agreed to its commencement before the deadline – contact: contact@2homes.app.</li>
              <li>Complaints: submit by email to contact@2homes.app with a description of the problem, account data, and proof of payment; we will consider it immediately, no later than 14 days.</li>
              <li>Refunds: in the case of an accepted complaint, the refund is made using the same payment method, if possible.</li>
            </ul>

            <h2><strong>17. Data After Contract Termination</strong></h2>
            <p>After termination of services, account data is subject to deletion in accordance with the Privacy Policy and legal regulations. Family Data: export on request; possible time-limited archiving before deletion.</p>

            <h2><strong>18. Changes to Terms</strong></h2>
            <p>We reserve the right to change the Terms. We will inform about significant changes (e.g., e-mail, banner). Continued use after the effective date means acceptance. If significant changes are made, we may require you to re-accept the updated Terms. Your previous acceptance records are maintained for audit purposes.</p>

            <h2><strong>19. Governing Law and Disputes</strong></h2>
            <p>Polish law. For consumers: rights under the Consumer Rights Act apply; out-of-court dispute resolution methods are possible. For entrepreneurs – court competent for the Administrator's seat.</p>

            <h2><strong>20. Contact</strong></h2>
            <p>E‑mail: contact@2homes.app<br />Correspondence address: ul. Kaszubska 6/21, 83-110 Tczew, Poland</p>

            <h2><strong>Annex A – Plan Specification</strong></h2>
            <div className="mt-2 space-y-2">
              <div>
                <div className="font-semibold">Basic (Solo) ($0)</div>
                <ul className="list-disc ml-5 text-slate-700">
                  <li>unlimited children</li>
                  <li>1 parent (Solo Mode)</li>
                  <li>history lock: last 5 entries</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold">Pro ($7.99/month or $79/year)</div>
                <ul className="list-disc ml-5 text-slate-700">
                  <li>unlimited children</li>
                  <li>2 parents + max 3 viewers</li>
                  <li>events, notes, expenses</li>
                  <li>reports (costs, care)</li>
                  <li>180-day history</li>
                  <li>unlimited calendar horizon</li>
                  <li>no ads</li>
                  <li>One pays, both use</li>
                </ul>
              </div>
              <div>
                <div className="font-semibold">Founder (Lifetime Deal) ($99 one-time)</div>
                <ul className="list-disc ml-5 text-slate-700">
                  <li>all Pro features</li>
                  <li>unlimited children</li>
                  <li>2 parents + max 3 viewers</li>
                  <li>events, notes, expenses</li>
                  <li>reports (costs, care)</li>
                  <li>180-day history</li>
                  <li>unlimited calendar horizon</li>
                  <li>no ads</li>
                  <li>lifetime access (Product Lifetime)</li>
                  <li>One pays, both use</li>
                  <li className="font-semibold text-amber-700">Limited to 50 spots</li>
                </ul>
              </div>
            </div>

            <h2><strong>Annex B – Data and Processors</strong></h2>
            <ul>
              <li>Hosting and CDN: Vercel (edge/static hosting).</li>
              <li>Database: Convex Inc. – reactive backend database.</li>
              <li>Authentication: Clerk Inc. – user authentication and identity.</li>
              <li>Email Sending: Resend.</li>
              <li>Cookie Consent/Consent Mode: vanilla-cookieconsent (open source).</li>
              <li>Analytics: Google Analytics 4 (with sensitive path masking and Consent Mode).</li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Terms
